import {DataTable} from "../DataTable/DataTable";
import {RoleEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Box, Modal, ModalProps} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useRoleDataTableFilter} from "../RoleDataTable/useRoleDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {useNavigate} from "react-router-dom";
import {useRoleAction} from "../../menu/RoleActionMenu/useRoleAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: RoleEntity[] = []

export type RoleDataTableProps = EntityDataTableProps<RoleEntity>

export const RoleDataTable = (props: RoleDataTableProps) => {
  const {
    records: roles = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useRoleDataTableFilter({
    records: roles
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
      "description": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.description?.toLowerCase()
          }])
        }
      }
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onRoleActionClick
  } = useRoleAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: RoleEntity) => {
    e.stopPropagation()
    navigate(`/roles/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: RoleEntity) => {
    e.stopPropagation()
    onRoleActionClick(record, 'EDIT')
  }, [onRoleActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: RoleEntity) => {
    e.stopPropagation()
    onRoleActionClick(record, 'DELETE')
  }, [onRoleActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<RoleEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.role.table.column.name", "Name"),
        accessor: 'name',
        width: 400,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record} />
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "description"),
        title: t("common.entity.role.table.description.person", "Description"),
        accessor: 'description',
        sortable: true,
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (role) => (
          <EntityActionGroup
            permissionPrefix={"role"}
            onViewActionClick={(e) => onViewActionClick(e, role)}
            onEditActionClick={(e) => onEditActionClick(e, role)}
            onDeleteActionClick={(e) => onDeleteActionClick(e, role)}
          />
        )
      }
    ]

    return values
  }, [filterConfig, onEditActionClick, onViewActionClick, onDeleteActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type RoleDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<RoleDataTableProps, 'recordsPerPageOptions'>
}

export const RoleDataTableModal = (props: RoleDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.role.plural", "Roles"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <RoleDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
